import { useEffect, useState } from "react";
import { CouponStatus, CouponType } from "@/pages/desktop-coupon/constant";
import { CouponDetailType } from "@/constant";
import { createContainer } from "unstated-next";
import { FormInstance } from "antd";
import { useLocation } from "react-router-dom";
import {
  ICouponDetailInfo,
  ICouponInfo,
  ICouponListInfo,
  ISelectAccountList,
  ISubAccountInfo,
} from "../types";
import request from "project-config/services/request/request";
import { CouponList } from "@/pages/desktop-coupon/constant/coupon-list";
import { accAdd } from "project-config/utils/acc";
import { URLSearchParamsHelper } from "project-config/utils/url-search";

export function useTabStoreHook() {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(CouponType.Desktop);
  const [currentStatus, setCurrentStatus] = useState(CouponStatus.All);
  const [transferCoupon, setTransferCoupon] = useState(0);
  const [couponId, setCouponId] = useState(0);
  const [currentAmount, setCurrentAmount] = useState("");
  const [exchangeModal, setExchangeModal] = useState(false);
  const [couponInfo, setCouponInfo] = useState<ICouponListInfo[]>();
  const [subAccountInfo, setSubAccountInfo] = useState<ISelectAccountList[]>(
    []
  );
  const [defaultSubAccount, setDefaultSubAccount] = useState<number | string>(
    ""
  );
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("");
  const [showRules, setShowRules] = useState(true);

  const cleanTransferForm = (
    form: FormInstance,
    defaultSubAccount: number | string
  ) => {
    form.setFieldsValue({ destUserId: defaultSubAccount, amount: "" });
    form.resetFields();

    setTransferCoupon(0);
    setCouponId(0);
  };

  const fetchUserCouponsList = async () => {
    const { data } = await request.post(
      "/api/rendering/pay/recharge/queryUserCoupons",
      {}
    );
    const { data: result } = await request.post(
      "/api/rendering/pay/recharge/queryUserVouchers"
    );
    return {
      data,
      result,
    };
  };

  const dealCouponsData = async () => {
    setLoading(true);
    const { data, result } = await fetchUserCouponsList();
    setLoading(false);
    if (!data || !result) return;
    const invalidCoupon = data.invalidCoupon.map((item: ICouponInfo) => {
      return { ...item, isExpired: true };
    });

    CouponList[0].data = data.effectiveCoupon
      .filter(
        (item: ICouponDetailInfo) =>
          item.type && [CouponDetailType.Desktop].includes(item.type)
      )
      .concat(
        invalidCoupon.filter(
          (item: ICouponDetailInfo) =>
            item.type && [CouponDetailType.Desktop].includes(item.type)
        )
      );

    CouponList[0].sum = CouponList[0].data.length;
    CouponList[0].amount = CouponList[0].data
      .filter((item: ICouponDetailInfo) => !item.isExpired)
      .reduce(function (acc: any, cur: ICouponDetailInfo) {
        return accAdd(acc, cur.couponBalance || 0);
      }, 0);
    setCouponInfo([...CouponList]);
  };

  const querySubAccount = async () => {
    const { data: subAccountResult } = await request.post(
      "/api/rendering/user/querySubUserList"
    );
    const { data: dingTalkSubAccountResult } = await request.post(
      "/api/rendering/user/queryDingTalkSubUserList",
      {}
    );
    return {
      subAccountResult,
      dingTalkSubAccountResult,
    };
  };

  const dealSubAccount = async () => {
    const { subAccountResult, dingTalkSubAccountResult } =
      await querySubAccount();
    if (!subAccountResult || !dingTalkSubAccountResult) return;
    const subAccountData: ISelectAccountList[] = [];
    const dingTalkSubAccountData: ISelectAccountList[] = [];
    subAccountResult.map((item: ISubAccountInfo) =>
      subAccountData.push({
        value: item.id,
        label: item.subUserName,
      })
    );
    dingTalkSubAccountResult.map((item: ISubAccountInfo) =>
      dingTalkSubAccountData.push({
        value: item.id,
        label: item.subUserName,
      })
    );
    const currentSubAccountInfo = [
      ...subAccountData,
      ...dingTalkSubAccountData,
    ];
    setSubAccountInfo(currentSubAccountInfo);
    setDefaultSubAccount(
      currentSubAccountInfo.length ? currentSubAccountInfo[0].value : ""
    );
  };

  useEffect(() => {
    const urlSearchParamsHelper = new URLSearchParamsHelper(location.search);
    const _defaultActiveKey =
      location?.state?.key ?? String(CouponType.Desktop);
    const activeName = urlSearchParamsHelper.get("activeName");
    setActiveKey(activeName ? String(activeName) : String(_defaultActiveKey));
    setCurrentTab(
      location?.search.split("=")[1]
        ? Number(location?.search.split("=")[1])
        : location?.state?.key ?? CouponType.Desktop
    );
    dealSubAccount();
    dealCouponsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return {
    currentTab,
    setCurrentTab,
    couponInfo,
    setCouponInfo,
    transferCoupon,
    setTransferCoupon,
    couponId,
    setCouponId,
    cleanTransferForm,
    subAccountInfo,
    defaultSubAccount,
    currentAmount,
    setCurrentAmount,
    currentStatus,
    setCurrentStatus,
    exchangeModal,
    setExchangeModal,
    dealCouponsData,
    activeKey,
    setActiveKey,
    loading,
    setLoading,
    setSubAccountInfo,
    setDefaultSubAccount,
    showRules,
    setShowRules,
  };
}

const TabStoreContainer = createContainer(useTabStoreHook);

export const TabStoreContainerProvider = TabStoreContainer.Provider;

export function useTabStore() {
  return TabStoreContainer.useContainer();
}
